import { useMutation, useQuery } from '@apollo/client';
import { type FC, useEffect, useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Text,
  Textarea,
  toast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useUserIdFromSession } from '@eluve/session-helpers';

/** Matches the backend prompt clamp (MAX_CUSTOM_INSTRUCTIONS_CHARS). */
const MAX_INSTRUCTIONS_CHARS = 2000;

const getTaskInstructionsQuery = graphql(`
  query getTaskExtractionInstructions($tenantId: uuid!, $userId: uuid!) {
    tenantUserSettingsByPk(tenantId: $tenantId, userId: $userId) {
      __typename
      tenantId
      userId
      customTaskExtractionInstructions
    }
  }
`);

const updateTaskInstructionsMutation = graphql(`
  mutation updateTaskExtractionInstructions(
    $tenantId: uuid!
    $userId: uuid!
    $instructions: String
  ) {
    updateTenantUserSettingsByPk(
      pkColumns: { tenantId: $tenantId, userId: $userId }
      _set: { customTaskExtractionInstructions: $instructions }
    ) {
      __typename
      tenantId
      userId
      customTaskExtractionInstructions
    }
  }
`);

export type TaskExtractionInstructionsDialogProps = {
  tenantId: string;
  onClose: () => void;
};

/**
 * "Instructions to auto-create tasks" (ENG-5451 alpha): the clinician's saved
 * free-text extraction preferences. Sticky per user+tenant
 * (tenant_user_settings, like custom note instructions) — every extraction of
 * their appointments applies them, automatic or regenerate. Save persists;
 * Reset clears the saved value (back to default extraction). Neither triggers
 * a regenerate — that's the panel's separate Regenerate button.
 */
export const TaskExtractionInstructionsDialog: FC<
  TaskExtractionInstructionsDialogProps
> = ({ tenantId, onClose }) => {
  const userId = useUserIdFromSession();
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  const { data, loading } = useQuery(getTaskInstructionsQuery, {
    variables: { tenantId, userId },
  });
  const saved =
    data?.tenantUserSettingsByPk?.customTaskExtractionInstructions ?? null;
  // Seed the textarea once the saved value arrives; the dialog mounts only
  // while open, so a re-open re-seeds from the latest save.
  useEffect(() => {
    setText(saved ?? '');
  }, [saved]);

  const [update] = useMutation(updateTaskInstructionsMutation, {
    onError: () => toast.error('Could not save the instructions.'),
  });

  const persist = async (instructions: string | null) => {
    setSaving(true);
    try {
      const res = await update({
        variables: { tenantId, userId, instructions },
      });
      if (!res.data?.updateTenantUserSettingsByPk) return false;
      return true;
    } catch {
      return false;
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    // Whitespace-only saves as cleared, same as Reset.
    const trimmed = text.trim();
    if (await persist(trimmed === '' ? null : trimmed)) {
      toast.success('Instructions saved.');
      onClose();
    }
  };

  const reset = async () => {
    setText('');
    if (saved === null) return;
    if (await persist(null)) toast.success('Instructions reset.');
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="xl" data-testid="clinical-tasks-instructions-dialog">
        <DialogHeader>
          <DialogTitle>Instructions to auto-create tasks</DialogTitle>
          <DialogDescription>
            Describe which follow-up tasks matter for your workflow, what to
            include, skip, or emphasize. Applies to every future extraction of
            your sessions; press Regenerate tasks to re-apply on this one.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={MAX_INSTRUCTIONS_CHARS}
          rows={6}
          placeholder="e.g. Always create a separate billing follow-up task; skip patient self-care instructions."
          disabled={loading || saving}
          data-testid="clinical-tasks-instructions-input"
        />
        <Text size="xs" color="tertiary">
          {text.length}/{MAX_INSTRUCTIONS_CHARS}
        </Text>
        <DialogFooter>
          <Button variant="outline" disabled={saving} onClick={reset}>
            Reset instructions
          </Button>
          <Button
            variant="brand"
            disabled={loading || saving}
            onClick={save}
            data-testid="clinical-tasks-instructions-save"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
